import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Username, useMask } from '../../utils/privacyMask';
import { getPackageTier, getAllTierNames, getTierColor, getPackagePlatformByTier } from '../../utils/packages';
import { getSubscriptionStatus } from '../../components/subscriptions/StatusChip';
import { computeRenewalScores } from '../../utils/renewalScore';
import './LeaderboardPage.css';

/* ── Constants ─────────────────────────────────────────────────────────── */
const TIME_RANGES = [
  { label: 'All Time', months: 0  },
  { label: '12 Months', months: 12 },
  { label: '6 Months',  months: 6  },
  { label: '3 Months',  months: 3  },
  { label: '1 Month',   months: 1  },
];

const ROW_LIMIT = 50;

// Tier names grouped by platform, for the package-filter dropdown's <optgroup>s
const PACKAGE_TIER_GROUPS = (() => {
  const groups = {};
  getAllTierNames().forEach(name => {
    const platform = getPackagePlatformByTier(name) || 'Other';
    if (!groups[platform]) groups[platform] = [];
    groups[platform].push(name);
  });
  return Object.entries(groups);
})();

function fmtMoney(n) { return `€${(parseFloat(n) || 0).toFixed(2)}`; }
function fmtCount(n, unit) { return `${n} ${unit}${n !== 1 ? 's' : ''}`; }
function fmtDuration(v) { return v >= 60 ? `${(v / 30.44).toFixed(1)} months` : fmtCount(v, 'day'); }

function withinMonths(dateVal, months) {
  if (!months) return true;
  const ts = Number(dateVal);
  if (!ts) return false;
  return ts >= Date.now() - months * 30.44 * 24 * 60 * 60 * 1000;
}

/* ── Unified leaderboard table ────────────────────────────────────────── */
function LeaderboardTable({ metric, entries, sortDir, pkgActive, pkgFilter }) {
  const [expandedId, setExpandedId] = useState(null);

  // Reset expansion when switching metrics/sort so stale detail rows don't linger
  useEffect(() => { setExpandedId(null); }, [metric.key, sortDir]);

  if (!entries.length) {
    return (
      <div className="lb-panel">
        <div className="lb-empty">
          <i className="bi bi-inbox" />
          No data for this filter
        </div>
      </div>
    );
  }

  const sorted = sortDir === 'desc' ? entries : [...entries].slice().reverse();
  const shown = sorted.slice(0, ROW_LIMIT);
  const rankClass = r => r === 2 ? 'lb-rank-2' : r === 3 ? 'lb-rank-3' : 'lb-rank-n';

  return (
    <div className="lb-panel">
      <div className="lb-panel-header">
        <h6 className="lb-panel-title">
          <i className={`bi ${metric.icon}`} style={{ color: metric.color }} />
          {metric.title}
        </h6>
        <span className="lb-panel-note">
          {pkgActive && metric.filterable ? pkgFilter : metric.note}
        </span>
      </div>

      {shown.map((entry, i) => {
        const rank = i + 1;
        const detail = metric.getDetail ? metric.getDetail(entry) : null;
        const isChampion = rank === 1 && sortDir === 'desc';
        const isOpen = expandedId === entry.id;
        return (
          <div key={entry.id}>
            <div
              className={isChampion ? 'lb-champion lb-table-row' : 'lb-entry lb-table-row'}
              onClick={() => detail && setExpandedId(isOpen ? null : entry.id)}
            >
              {isChampion ? (
                <span className="lb-champion-crown">🏆</span>
              ) : (
                <div className={`lb-rank ${rankClass(rank)}`}>{rank}</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={isChampion ? 'lb-champion-name' : 'lb-entry-name'}><Username value={entry.name} /></div>
              </div>
              <div className={isChampion ? 'lb-champion-value' : 'lb-entry-value'} style={isChampion ? {} : { color: metric.color }}>
                {metric.formatValue(entry.value)}
              </div>
              {detail && (
                <i className={`bi bi-chevron-right lb-expand-chevron ${isOpen ? 'open' : ''}`} style={{ fontSize: '0.7rem' }} />
              )}
            </div>
            {isOpen && detail && <div className="lb-table-detail">{detail}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────── */
export default function LeaderboardPage() {
  const { mask } = useMask();
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions]   = useState([]);
  const [referrals, setReferrals]         = useState([]);
  const [accounts, setAccounts]           = useState([]);
  const [requests, setRequests]           = useState([]);
  const [issues, setIssues]               = useState([]);
  const [points, setPoints]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const [timeRange, setTimeRange]     = useState(0);   // months (0 = all)
  const [pkgFilter, setPkgFilter]     = useState('All Packages');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [subRes, txRes, refRes, accRes, reqRes, issueRes, ptsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription?all=true`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`,           { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/referral`,              { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`,               { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/botdata/requests`,      { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/botdata/issues`,        { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/referralpoints`,        { headers }).catch(() => ({ data: [] })),
        ]);
        setSubscriptions(subRes.data   || []);
        setTransactions(txRes.data     || []);
        setReferrals(refRes.data       || []);
        setAccounts(accRes.data        || []);
        setRequests(reqRes.data        || []);
        setIssues(issueRes.data        || []);
        setPoints(Array.isArray(ptsRes.data) ? ptsRes.data : []);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  /* ── Name map (accountID → username) ── */
  const nameMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a._id] = a.username; });
    return m;
  }, [accounts]);

  /* ── Discord → display name map ── */
  const discordMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { if (a.discord) m[a.discord] = a.username; });
    return m;
  }, [accounts]);

  /* ── Discord ID → display name map (for legacy discordId-only points records) ── */
  const discordIdMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { if (a.discordID) m[a.discordID] = a.username; });
    return m;
  }, [accounts]);

  /* ── Lifetime Points leaderboard ── */
  const pointsBoard = useMemo(() =>
    points
      .map(p => ({
        id: p.accountID || p.discordId,
        name: (p.accountID && nameMap[p.accountID]) || (p.discordId && discordIdMap[p.discordId]) || p.discordUsername || p.discordId || p.accountID,
        value: p.totalPointsEarned || 0,
      }))
      .filter(e => e.value > 0)
      .sort((a, b) => b.value - a.value),
    [points, nameMap, discordIdMap]
  );

  /* ── Longest Subscribed (currently active + not expired only) ── */
  const longestSubscribedBoard = useMemo(() => {
    const byAccount = {};
    subscriptions.forEach(s => {
      if (!s.accountID) return;
      if (!byAccount[s.accountID]) byAccount[s.accountID] = [];
      byAccount[s.accountID].push(s);
    });

    return Object.entries(byAccount)
      .map(([accountID, subs]) => {
        const mostRecent = subs.reduce((a, b) => (Number(a.createdDate) || 0) > (Number(b.createdDate) || 0) ? a : b);
        const status = getSubscriptionStatus(mostRecent);
        if (status === 'Suspended' || status === 'Expired') return null;
        const earliest = Math.min(...subs.map(s => Number(s.createdDate)).filter(Number.isFinite));
        if (!Number.isFinite(earliest)) return null;
        const days = Math.floor((Date.now() - earliest) / 86400000);
        return { id: accountID, name: nameMap[accountID] || accountID, value: days };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value);
  }, [subscriptions, nameMap]);

  /* ── Renewal Score — shared with AnalysisPage.js so both show identical numbers ── */
  const renewalScoreBoard = useMemo(
    () => computeRenewalScores(accounts, subscriptions, requests, issues),
    [subscriptions, requests, issues, accounts]
  );

  /* ── Requests leaderboard (by discord username) ── */
  const requestBoard = useMemo(() => {
    const counts = {};
    requests.forEach(r => {
      if (!r.username) return;
      counts[r.username] = (counts[r.username] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([discord, count]) => ({ id: discord, name: discordMap[discord] || discord, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [requests, discordMap]);

  /* ── Issues leaderboard (by discord username) ── */
  const issueBoard = useMemo(() => {
    const counts = {};
    issues.forEach(i => {
      if (!i.memberUsername) return;
      counts[i.memberUsername] = (counts[i.memberUsername] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([discord, count]) => ({ id: discord, name: discordMap[discord] || discord, value: count }))
      .sort((a, b) => b.value - a.value);
  }, [issues, discordMap]);

  /* ── Filtered slices ── */
  const filteredSubs = useMemo(() =>
    subscriptions.filter(s =>
      withinMonths(s.createdDate, timeRange) &&
      (pkgFilter === 'All Packages' || getPackageTier(s.package) === pkgFilter)
    ),
    [subscriptions, timeRange, pkgFilter]
  );

  // Active subs: package filter but no time filter (current state)
  const activeSubsFiltered = useMemo(() =>
    subscriptions.filter(s =>
      s.active &&
      (pkgFilter === 'All Packages' || getPackageTier(s.package) === pkgFilter)
    ),
    [subscriptions, pkgFilter]
  );

  const filteredTxs = useMemo(() =>
    transactions.filter(t => withinMonths(t.transactionDate, timeRange)),
    [transactions, timeRange]
  );

  const filteredRefs = useMemo(() =>
    referrals.filter(r => withinMonths(r.referDate, timeRange)),
    [referrals, timeRange]
  );

  /* ── Aggregate per account ── */
  const stats = useMemo(() => {
    const map = {};

    const ensure = id => {
      if (!map[id]) map[id] = {
        id,
        name:          nameMap[id] || id,
        subRevenue:    0,
        subProfit:     0,
        totalSubs:     0,
        activeSubs:    0,
        txRevenue:     0,
        txProfit:      0,
        referralsMade: 0,
        referralsRecv: 0,
      };
      return map[id];
    };

    filteredSubs.forEach(s => {
      const a = ensure(s.accountID);
      a.subRevenue += parseFloat(s.paidPrice)      || 0;
      a.subProfit  += parseFloat(s.profitReceived) || 0;
      a.totalSubs  += 1;
    });

    activeSubsFiltered.forEach(s => {
      ensure(s.accountID).activeSubs += 1;
    });

    filteredTxs.forEach(t => {
      const a = ensure(t.accountID);
      a.txRevenue += parseFloat(t.revenue) || 0;
      a.txProfit  += parseFloat(t.profit)  || 0;
    });

    filteredRefs.forEach(r => {
      if (r.referer) ensure(r.referer).referralsMade += 1;
      if (r.refered) ensure(r.refered).referralsRecv += 1;
    });

    return Object.values(map);
  }, [filteredSubs, activeSubsFiltered, filteredTxs, filteredRefs, nameMap]);

  /* ── Sorted leaderboard arrays ── */
  const ranked = (key) =>
    stats
      .filter(a => a[key] > 0)
      .sort((a, b) => b[key] - a[key])
      .map(a => ({ id: a.id, name: a.name, value: a[key], raw: a }));

  const boards = useMemo(() => ({
    subRevenue:    ranked('subRevenue'),
    subProfit:     ranked('subProfit'),
    activeSubs:    ranked('activeSubs'),
    totalSubs:     ranked('totalSubs'),
    txRevenue:     ranked('txRevenue'),
    referralsMade: ranked('referralsMade'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stats]);

  /* ── Metric registry — one entry per selectable leaderboard ── */
  const METRICS = useMemo(() => [
    { key: 'subRevenue',    title: 'Subscription Revenue', icon: 'bi-cash-stack',                 color: '#34d399', entries: boards.subRevenue,
      formatValue: v => mask(fmtMoney(v), 'money'), filterable: true,
      getDetail: e => `${fmtCount(e.raw.totalSubs, 'subscription')} · profit ${mask(fmtMoney(e.raw.subProfit), 'money')}` },
    { key: 'subProfit',     title: 'Subscription Profit',  icon: 'bi-graph-up-arrow',             color: '#38bdf8', entries: boards.subProfit,
      formatValue: v => mask(fmtMoney(v), 'money'), filterable: true,
      getDetail: e => `revenue ${mask(fmtMoney(e.raw.subRevenue), 'money')} · ${fmtCount(e.raw.totalSubs, 'subscription')}` },
    { key: 'activeSubs',    title: 'Active Subscriptions', icon: 'bi-bookmark-check-fill',        color: '#4ade80', entries: boards.activeSubs,
      formatValue: v => fmtCount(v, 'active sub'), filterable: true, note: 'current state',
      getDetail: e => `total all-time: ${e.raw.totalSubs}` },
    { key: 'totalSubs',     title: 'Total Subscriptions',  icon: 'bi-bookmark-star-fill',         color: '#fbbf24', entries: boards.totalSubs,
      formatValue: v => fmtCount(v, 'subscription'), filterable: true,
      getDetail: e => `${mask(fmtMoney(e.raw.subRevenue), 'money')} revenue · ${e.raw.activeSubs} active` },
    { key: 'txRevenue',     title: 'Transaction Revenue',  icon: 'bi-receipt',                    color: '#a78bfa', entries: boards.txRevenue,
      formatValue: v => mask(fmtMoney(v), 'money'), note: 'transactions only',
      getDetail: e => `profit ${mask(fmtMoney(e.raw.txProfit), 'money')}` },
    { key: 'referralsMade', title: 'Referrals Made',       icon: 'bi-share-fill',                 color: '#fb923c', entries: boards.referralsMade,
      formatValue: v => fmtCount(v, 'referral'), note: 'as referrer',
      getDetail: e => `${e.raw.referralsRecv || 0} referrals received` },
    { key: 'requests',      title: 'Most Bot Requests',    icon: 'bi-inbox-fill',                  color: '#60a5fa', entries: requestBoard,
      formatValue: v => fmtCount(v, 'request'), note: 'all time' },
    { key: 'issues',        title: 'Most Issues Raised',   icon: 'bi-exclamation-triangle-fill',   color: '#fbbf24', entries: issueBoard,
      formatValue: v => fmtCount(v, 'issue'), note: 'all time' },
    { key: 'points',        title: 'Lifetime Points',      icon: 'bi-star-fill',                   color: '#4F46E5', entries: pointsBoard,
      formatValue: v => v.toLocaleString(), note: 'all time' },
    { key: 'longestSubscribed', title: 'Longest Subscribed', icon: 'bi-hourglass-split',           color: '#06b6d4', entries: longestSubscribedBoard,
      formatValue: fmtDuration, note: 'currently active only' },
    { key: 'renewalScore',  title: 'Renewal Score',        icon: 'bi-arrow-repeat',                color: '#f472b6', entries: renewalScoreBoard,
      formatValue: v => `${v}/100`, note: 'heuristic — see docs',
      getDetail: e => e.raw.subCount > 1 ? `${e.raw.subCount} subscriptions` : `${e.raw.activityCount} activity events` },
  ], [boards, requestBoard, issueBoard, pointsBoard, longestSubscribedBoard, renewalScoreBoard, mask]);

  const [selectedMetricKey, setSelectedMetricKey] = useState('subRevenue');
  const [sortDir, setSortDir] = useState('desc');
  const selectedMetric = METRICS.find(m => m.key === selectedMetricKey) || METRICS[0];

  /* ── Summary totals ── */
  const totals = useMemo(() => ({
    subRevenue:    filteredSubs.reduce((s, x) => s + (parseFloat(x.paidPrice)      || 0), 0),
    subProfit:     filteredSubs.reduce((s, x) => s + (parseFloat(x.profitReceived) || 0), 0),
    txRevenue:     filteredTxs.reduce( (s, x) => s + (parseFloat(x.revenue)        || 0), 0),
    activeSubs:    activeSubsFiltered.length,
    totalSubs:     filteredSubs.length,
    referrals:     filteredRefs.length,
    accounts:      new Set([...filteredSubs.map(s => s.accountID), ...filteredTxs.map(t => t.accountID)]).size,
  }), [filteredSubs, filteredTxs, filteredRefs, activeSubsFiltered]);

  if (loading) return <LoadingSpinner />;

  const timeLabel = TIME_RANGES.find(t => t.months === timeRange)?.label || 'All Time';
  const pkgActive = pkgFilter !== 'All Packages';

  return (
    <>
      <Sidebar title="Leaderboard" />
      <div className="lb-page">

        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        {/* ── Filters ── */}
        <div className="lb-filter-bar" style={{ marginBottom: 20 }}>
          <span className="lb-filter-label">Period</span>
          {TIME_RANGES.map(t => (
            <button
              key={t.months}
              className={`lb-tab ${timeRange === t.months ? 'active' : ''}`}
              onClick={() => setTimeRange(t.months)}
            >
              {t.label}
            </button>
          ))}

          <span className="lb-filter-sep" />

          <span className="lb-filter-label">Package</span>
          <select
            className="lb-metric-select"
            value={pkgFilter}
            onChange={e => setPkgFilter(e.target.value)}
            style={pkgActive ? { borderColor: `${getTierColor(pkgFilter)}66`, color: getTierColor(pkgFilter) } : {}}
          >
            <option value="All Packages">All Packages</option>
            {PACKAGE_TIER_GROUPS.map(([platform, tiers]) => (
              <optgroup key={platform} label={platform}>
                {tiers.map(tier => <option key={tier} value={tier}>{tier}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {/* ── Summary bar ── */}
        <div className="lb-summary">
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Sub Revenue</span>
            <span className="lb-summary-value" style={{ color: '#34d399' }}>{mask(fmtMoney(totals.subRevenue), 'money')}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Sub Profit</span>
            <span className="lb-summary-value" style={{ color: '#38bdf8' }}>{mask(fmtMoney(totals.subProfit), 'money')}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Tx Revenue</span>
            <span className="lb-summary-value" style={{ color: '#a78bfa' }}>{mask(fmtMoney(totals.txRevenue), 'money')}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Active Subs</span>
            <span className="lb-summary-value" style={{ color: '#4ade80' }}>{totals.activeSubs}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Total Subs</span>
            <span className="lb-summary-value" style={{ color: '#fbbf24' }}>{totals.totalSubs}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Referrals</span>
            <span className="lb-summary-value" style={{ color: '#fb923c' }}>{totals.referrals}</span>
          </div>
          <div className="lb-summary-sep" />
          <div className="lb-summary-stat">
            <span className="lb-summary-label">Accounts</span>
            <span className="lb-summary-value" style={{ color: 'var(--color-text)' }}>{totals.accounts}</span>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)' }}>{timeLabel}</span>
            {pkgActive && <span style={{ fontSize: '0.7rem', color: getTierColor(pkgFilter) }}>{pkgFilter}</span>}
          </div>
        </div>

        {/* ── Metric toolbar ── */}
        <div className="lb-metric-bar">
          <select
            className="lb-metric-select"
            value={selectedMetricKey}
            onChange={e => setSelectedMetricKey(e.target.value)}
          >
            {METRICS.map(m => <option key={m.key} value={m.key}>{m.title}</option>)}
          </select>
          <button type="button" className="lb-sort-btn" onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>
            <i className={`bi bi-sort-${sortDir === 'desc' ? 'down' : 'up'}`} />
            {sortDir === 'desc' ? 'Highest first' : 'Lowest first'}
          </button>
          {selectedMetric.entries.length > ROW_LIMIT && (
            <span className="lb-row-cap-note">showing top {ROW_LIMIT} of {selectedMetric.entries.length}</span>
          )}
        </div>

        <LeaderboardTable
          metric={selectedMetric}
          entries={selectedMetric.entries}
          sortDir={sortDir}
          pkgActive={pkgActive}
          pkgFilter={pkgFilter}
        />
      </div>
    </>
  );
}
